// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, createContext, useCallback, useMemo, useState } from 'react';

import { useGeneralContext } from 'helpers/useGeneralContext';

declare const modalIdSymbol: unique symbol;
/** Branded with the modal's name, so an id can't be paired with another modal's props */
export type ModalId<K extends string = string> = string & { [modalIdSymbol]: K };

type ModalStackContextType = {
  /** The most recently opened modal that is still open, across every modal registry */
  topModalId: ModalId | undefined;
  push: (modalId: ModalId) => void;
  remove: (modalId: ModalId) => void;
};

const ModalStackContext = createContext<ModalStackContextType | undefined>(undefined);

/**
 * Orders the open modals of every `createModalRegistry` registry, so that only the most recently opened one
 * is shown, whichever registry it belongs to. Mounted once, at the app root.
 */
export const ModalStackProvider = ({ children }: { children: ReactNode }) => {
  const [stack, setStack] = useState<ModalId[]>([]);

  const push = useCallback<ModalStackContextType['push']>((modalId) => {
    setStack((s) => (s.includes(modalId) ? s : [...s, modalId]));
  }, []);

  const remove = useCallback<ModalStackContextType['remove']>((modalId) => {
    setStack((s) => s.filter((id) => id !== modalId));
  }, []);

  const topModalId = stack[stack.length - 1];

  const value = useMemo(() => ({ topModalId, push, remove }), [topModalId, push, remove]);

  return <ModalStackContext value={value}>{children}</ModalStackContext>;
};

/** Used by `createModalRegistry`; registries share this stack instead of each keeping their own. */
export const useModalStack = () => useGeneralContext(ModalStackContext, 'ModalStack');
